import axios from 'axios';
import { getCookieValue } from '../App';
import { AppThunk } from '../app/store';
import { createSlice, Dispatch, PayloadAction } from '@reduxjs/toolkit';
import { getStartAndEndDates } from '../utils/generalFunctions';
import {
  FilterByDate,
  ReferralDataRequest,
  ReferralDataResponse,
  ReferralTreeNode,
  ReferralUserChildrenRequest,
} from '../utils/type';
import { handleAxiosError } from '../assets/helper';

interface ReportingState {
  graphTransactionValues: any | null;
  graphPartnerEarnings: any | null;
  graphMemberEarnings: any | null;
  campaignData: any | null;
  graphNewEnrolledCard: any | null;
  graphTotalEnrolledCard: any | null;
  graphDonationTotals: any | null;
  donationReports: any | null;
  referralData: ReferralDataResponse | null;
  referralTreeData: ReferralTreeNode[] | null;
  loading: boolean;
  loadingStates: { [key: string]: boolean };
}
const initialState: ReportingState = {
  graphTransactionValues: null,
  graphPartnerEarnings: null,
  graphMemberEarnings: null,
  campaignData: null,
  graphNewEnrolledCard: null,
  graphTotalEnrolledCard: null,
  graphDonationTotals: null,
  donationReports: null,
  referralData: null,
  referralTreeData: null,
  loading: false,
  loadingStates: {},
};

export const setLoadingStatesFunction = (
  dispatch: Dispatch,
  loadingKey: string
) => {
  dispatch(setLoadingState({ key: loadingKey, value: false }));
};

const ReportingSlice = createSlice({
  name: 'partner',
  initialState,
  reducers: {
    setGraphTransactionValues: (state, action: PayloadAction<any>) => {
      state.graphTransactionValues = action.payload;
    },
    setGraphPartnerEarnings: (state, action: PayloadAction<any>) => {
      state.graphPartnerEarnings = action.payload;
    },
    setGraphMemberEarnings: (state, action: PayloadAction<any>) => {
      state.graphMemberEarnings = action.payload;
    },
    setGraphCampaignData: (state, action: PayloadAction<any>) => {
      state.campaignData = action.payload;
    },

    setGraphNewEnrolledCard: (state, action: PayloadAction<any>) => {
      state.graphNewEnrolledCard = action.payload;
    },
    setGraphTotalEnrolledCard: (state, action: PayloadAction<any>) => {
      state.graphTotalEnrolledCard = action.payload;
    },
    setGraphDonationTotals: (state, action: PayloadAction<any>) => {
      state.graphDonationTotals = action.payload;
    },
    setDonationReports: (state, action: PayloadAction<any>) => {
      state.donationReports = action.payload;
    },

    setReferralData: (state, action: PayloadAction<ReferralDataResponse>) => {
      state.referralData = action.payload;
    },

    setReferralTreeData: (state, action: PayloadAction<ReferralTreeNode[]>) => {
      state.referralTreeData = action.payload;
    },

    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setLoadingState: (
      state,
      action: PayloadAction<{ key: string; value: boolean }>
    ) => {
      state.loadingStates[action.payload.key] = action.payload.value;
    },
  },
});
export const {
  setLoading,
  setLoadingState,
  setGraphTransactionValues,
  setGraphPartnerEarnings,
  setGraphMemberEarnings,
  setGraphCampaignData,
  setGraphNewEnrolledCard,
  setGraphTotalEnrolledCard,
  setGraphDonationTotals,
  setDonationReports,
  setReferralData,
  setReferralTreeData,
} = ReportingSlice.actions;

export const GetGraphTransactionValues =
  (date?: FilterByDate): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = 'GetGraphTransactionValues';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/t/v`,
          date ?? getStartAndEndDates(),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGraphTransactionValues(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);

      return error;
    }
  };
export const GetGraphPartnerEarnings =
  (date?: FilterByDate): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = 'GetGraphPartnerEarnings';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/e/p`,
          date ?? getStartAndEndDates(),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGraphPartnerEarnings(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);

      return error;
    }
  };
export const GetGraphMemberEarnings =
  (date?: FilterByDate): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = 'GetGraphMemberEarnings';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/e/m`,
          date ?? getStartAndEndDates(),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGraphMemberEarnings(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);

      return error;
    }
  };
export const GetGraphCampaignData =
  (): AppThunk => async (dispatch, getState) => {
    const loadingKey = 'GetGraphCampaignData';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/e/s/r`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGraphCampaignData(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);

      return error;
    }
  };
export const GetGraphNewEnrolledCard =
  (date?: FilterByDate): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = 'GetGraphNewEnrolledCard';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/c/n`,
          date ?? getStartAndEndDates(),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGraphNewEnrolledCard(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);
      return error;
    }
  };
export const GetGraphTotalEnrolledCard =
  (date?: FilterByDate): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = 'GetGraphTotalEnrolledCard';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/c`,
          date ?? getStartAndEndDates(),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGraphTotalEnrolledCard(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);
      return error;
    }
  };

// Get Referral Data
export const GetReferralData =
  (params: ReferralDataRequest): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = 'GetReferralData';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const requestPayload = {
          limit: params.limit,
          offset: params.offset || 0,
          sortBy: params.sortBy,
          order: params.order,
          filterBy: params.filterBy,
          exportToCSV: params.exportToCSV || false,
        };

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/r/d`,
          requestPayload,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 200) {
          dispatch(setReferralData(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);
      return error;
    }
  };

export const GetReferralTreeData =
  (): AppThunk => async (dispatch, getState) => {
    const loadingKey = 'GetReferralTreeData';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/r/t`,
          {},
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 200) {
          dispatch(setReferralTreeData(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);
      return error;
    }
  };

// Get Referral User Children
export const GetReferralUserChildren =
  (params: ReferralUserChildrenRequest): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = `GetReferralUserChildren_${params.userId}`;
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/r/u/t`,
          params,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.status === 200) {
          // Return the children data instead of updating state directly
          // The TreeTable component will handle updating its own state
          setLoadingStatesFunction(dispatch, loadingKey);
          return response.data; // Return children array from response
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);
      return null;
    }
  };

export const GetGraphDonationTotals =
  (date?: FilterByDate): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = 'GetGraphDonationTotals';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/p/d`,
          date ?? getStartAndEndDates(),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setGraphDonationTotals(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);
      return error;
    }
  };

export const GetDonationReports =
  (date?: FilterByDate): AppThunk =>
  async (dispatch, getState) => {
    const loadingKey = 'GetDonationReports';
    try {
      const { reporting } = getState() as { reporting: ReportingState };
      const token = getCookieValue('token');
      if (!reporting.loadingStates[loadingKey] && token) {
        dispatch(setLoadingState({ key: loadingKey, value: true }));

        const response = await axios.post(
          `${process.env.REACT_APP_ADMIN_BASE_URL}/rpt/p/d/r`,
          date ?? getStartAndEndDates(),
          {
            headers: {
              'Authorization': `Bearer ${token}`,
              'x-api-key': process.env.REACT_APP_ADMIN_X_API_KEY,
            },
          }
        );

        if (response.status === 200) {
          dispatch(setDonationReports(response.data));
          setLoadingStatesFunction(dispatch, loadingKey);
        }
      }
    } catch (error) {
      handleAxiosError(error, dispatch);
      setLoadingStatesFunction(dispatch, loadingKey);
      return error;
    }
  };

export const ReportingReducer = ReportingSlice.reducer;
